import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Store, Plus, Search, Filter, Pencil, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const categories = [
  { value: 'anchor', label: 'Anchor' },
  { value: 'vanilla', label: 'Vanilla' },
  { value: 'kiosk', label: 'Kiosk' },
  { value: 'popup', label: 'Pop-up' },
  { value: 'food_court', label: 'Food Court' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'services', label: 'Services' },
];

const statuses = [
  { value: 'vacant', label: 'Vacant' },
  { value: 'occupied', label: 'Occupied' },
  { value: 'reserved', label: 'Reserved' },
  { value: 'under_renovation', label: 'Under Renovation' },
];

export default function InventoryPage() {
  const { api } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shops, setShops] = useState([]);
  const [malls, setMalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMall, setFilterMall] = useState(searchParams.get('mall') || 'all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingShop, setEditingShop] = useState(null);
  const [formData, setFormData] = useState({
    mall_id: '',
    unit_number: '',
    floor: 0,
    carpet_area_sqft: 0,
    buildup_area_sqft: 0,
    category: 'vanilla',
    status: 'vacant',
    rent_per_sqft: 0,
    brand_name: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filterMall]);

  const fetchData = async () => {
    try {
      const mallsRes = await api.get('/malls');
      setMalls(mallsRes.data);

      const params = new URLSearchParams();
      if (filterMall && filterMall !== 'all') {
        params.append('mall_id', filterMall);
      }

      const shopsRes = await api.get(`/shops?${params.toString()}`);
      setShops(shopsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        ...formData,
        floor: parseInt(formData.floor) || 0,
        carpet_area_sqft: parseFloat(formData.carpet_area_sqft) || 0,
        buildup_area_sqft: parseFloat(formData.buildup_area_sqft) || parseFloat(formData.carpet_area_sqft) * 1.2,
        rent_per_sqft: parseFloat(formData.rent_per_sqft) || 0,
      };

      if (editingShop) {
        await api.put(`/shops/${editingShop.id}`, {
          status: data.status,
          brand_name: data.brand_name,
          rent_per_sqft: data.rent_per_sqft,
          category: data.category,
        });
        toast.success('Shop updated successfully');
      } else {
        await api.post('/shops', data);
        toast.success('Shop created successfully');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save shop');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (shop) => {
    setEditingShop(shop);
    setFormData({
      mall_id: shop.mall_id,
      unit_number: shop.unit_number,
      floor: shop.floor,
      carpet_area_sqft: shop.carpet_area_sqft,
      buildup_area_sqft: shop.buildup_area_sqft,
      category: shop.category,
      status: shop.status,
      rent_per_sqft: shop.rent_per_sqft,
      brand_name: shop.brand_name || '',
    });
    setDialogOpen(true);
  };

  const openNewDialog = () => {
    setEditingShop(null);
    setFormData({
      mall_id: filterMall !== 'all' ? filterMall : malls[0]?.id || '',
      unit_number: '',
      floor: 0,
      carpet_area_sqft: 500,
      buildup_area_sqft: 600,
      category: 'vanilla',
      status: 'vacant',
      rent_per_sqft: 200,
      brand_name: '',
    });
    setDialogOpen(true);
  };

  const filteredShops = shops.filter((shop) => {
    const matchesSearch =
      shop.unit_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      shop.brand_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || shop.status === filterStatus;
    const matchesCategory = filterCategory === 'all' || shop.category === filterCategory;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  // Group by floor for grid view
  const shopsByFloor = filteredShops.reduce((acc, shop) => {
    const floor = shop.floor;
    if (!acc[floor]) acc[floor] = [];
    acc[floor].push(shop);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Inventory</h2>
          <p className="text-muted-foreground">{filteredShops.length} shops across {malls.length} malls</p>
        </div>
        <Button className="btn-primary" onClick={openNewDialog} data-testid="add-shop-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Shop
        </Button>
      </div>

      {/* Filters */}
      <Card className="glass-card">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="search-input-wrapper lg:col-span-2">
              <Search className="search-icon w-4 h-4" />
              <Input
                placeholder="Search by unit or brand..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-dark pl-10"
                data-testid="search-shops"
              />
            </div>
            <Select value={filterMall} onValueChange={(v) => { setFilterMall(v); setSearchParams(v !== 'all' ? { mall: v } : {}); }}>
              <SelectTrigger className="input-dark" data-testid="filter-mall">
                <SelectValue placeholder="All Malls" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Malls</SelectItem>
                {malls.map((mall) => (
                  <SelectItem key={mall.id} value={mall.id}>
                    {mall.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="input-dark" data-testid="filter-status">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {statuses.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="input-dark" data-testid="filter-category">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* View Tabs */}
      <Tabs defaultValue="grid" className="space-y-4">
        <TabsList className="bg-muted/30">
          <TabsTrigger value="grid">Grid View</TabsTrigger>
          <TabsTrigger value="table">Table View</TabsTrigger>
        </TabsList>

        <TabsContent value="grid">
          {Object.keys(shopsByFloor).sort((a, b) => a - b).map((floor) => (
            <div key={floor} className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Floor {floor}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {shopsByFloor[floor].map((shop, index) => (
                  <div
                    key={shop.id}
                    onClick={() => handleEdit(shop)}
                    className={`shop-card p-3 rounded-lg border cursor-pointer transition-all hover:scale-105 ${
                      shop.status === 'occupied'
                        ? 'bg-emerald-500/10 border-emerald-500/30 hover:border-emerald-500'
                        : shop.status === 'vacant'
                        ? 'bg-amber-500/10 border-amber-500/30 hover:border-amber-500'
                        : 'bg-blue-500/10 border-blue-500/30 hover:border-blue-500'
                    }`}
                    style={{ animationDelay: `${index * 0.02}s` }}
                    data-testid={`shop-card-${shop.id}`}
                  >
                    <p className="font-mono text-sm font-medium">{shop.unit_number}</p>
                    <p className="text-xs text-muted-foreground truncate mt-1">
                      {shop.brand_name || shop.category}
                    </p>
                    <p className="text-xs font-mono mt-1">{shop.carpet_area_sqft} sqft</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="table">
          <Card className="glass-card">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Unit</th>
                      <th>Mall</th>
                      <th>Floor</th>
                      <th>Brand</th>
                      <th>Category</th>
                      <th>Area (sqft)</th>
                      <th>Rent/sqft</th>
                      <th>Monthly Rent</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredShops.slice(0, 50).map((shop) => (
                      <tr key={shop.id}>
                        <td className="font-mono font-medium">{shop.unit_number}</td>
                        <td className="text-muted-foreground">{shop.mall_name}</td>
                        <td>Floor {shop.floor}</td>
                        <td>{shop.brand_name || '-'}</td>
                        <td className="capitalize">{shop.category.replace('_', ' ')}</td>
                        <td className="font-mono">{shop.carpet_area_sqft}</td>
                        <td className="font-mono">₹{shop.rent_per_sqft}</td>
                        <td className="font-mono">₹{shop.monthly_rent?.toLocaleString()}</td>
                        <td>
                          <Badge
                            className={`status-badge ${
                              shop.status === 'occupied'
                                ? 'status-occupied'
                                : shop.status === 'vacant'
                                ? 'status-vacant'
                                : 'status-reserved'
                            }`}
                          >
                            {shop.status}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(shop)}
                            data-testid={`edit-shop-${shop.id}`}
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {filteredShops.length === 0 && (
        <div className="text-center py-12">
          <Store className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No shops found</p>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="glass-panel sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingShop ? 'Edit Shop' : 'Add New Shop'}</DialogTitle>
            <DialogDescription>
              {editingShop ? 'Update shop details' : 'Add a new shop to inventory'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              {!editingShop && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="mall">Mall *</Label>
                      <Select
                        value={formData.mall_id}
                        onValueChange={(v) => setFormData({ ...formData, mall_id: v })}
                      >
                        <SelectTrigger className="input-dark" data-testid="shop-mall-select">
                          <SelectValue placeholder="Select mall" />
                        </SelectTrigger>
                        <SelectContent>
                          {malls.map((mall) => (
                            <SelectItem key={mall.id} value={mall.id}>
                              {mall.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="unit">Unit Number *</Label>
                      <Input
                        id="unit"
                        value={formData.unit_number}
                        onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
                        className="input-dark"
                        placeholder="e.g., F1-001"
                        required
                        data-testid="shop-unit-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="floor">Floor</Label>
                      <Input
                        id="floor"
                        type="number"
                        value={formData.floor}
                        onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
                        className="input-dark"
                        min={0}
                        data-testid="shop-floor-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="area">Carpet Area (sqft)</Label>
                      <Input
                        id="area"
                        type="number"
                        value={formData.carpet_area_sqft}
                        onChange={(e) => setFormData({ ...formData, carpet_area_sqft: e.target.value })}
                        className="input-dark"
                        data-testid="shop-area-input"
                      />
                    </div>
                  </div>
                </>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(v) => setFormData({ ...formData, category: v })}
                  >
                    <SelectTrigger className="input-dark" data-testid="shop-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(v) => setFormData({ ...formData, status: v })}
                  >
                    <SelectTrigger className="input-dark" data-testid="shop-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statuses.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="rent">Rent per sqft (₹)</Label>
                  <Input
                    id="rent"
                    type="number"
                    value={formData.rent_per_sqft}
                    onChange={(e) => setFormData({ ...formData, rent_per_sqft: e.target.value })}
                    className="input-dark"
                    data-testid="shop-rent-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="brand">Brand Name</Label>
                  <Input
                    id="brand"
                    value={formData.brand_name}
                    onChange={(e) => setFormData({ ...formData, brand_name: e.target.value })}
                    className="input-dark"
                    placeholder="e.g., Zara"
                    data-testid="shop-brand-input"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-shop-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
